import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapLocation } from '../utils/MarkerClusterManager';

interface InternationalMetaTagsManagerProps {
  selectedEntry?: MapLocation['entry'] | null;
  pageType?: 'homepage' | 'restaurant' | 'about' | 'faq' | 'howto' | 'privacy';
  customTitle?: string;
  customDescription?: string;
}

const InternationalMetaTagsManager: React.FC<InternationalMetaTagsManagerProps> = ({
  selectedEntry,
  pageType = 'homepage',
  customTitle,
  customDescription
}) => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const updateMetaTags = () => {
      const language = i18n.language;
      
      // Generate page-specific SEO data
      let title: string;
      let description: string;
      let keywords: string[];

      switch (pageType) {
        case 'restaurant':
          if (selectedEntry) {
            const restaurantName = selectedEntry.name || 'Restaurant';
            title = `${restaurantName} - ${t('seo.keywords.foodSafety')} | Kauwen of Spauwen`;
            description = `${t('restaurant.details')} ${restaurantName}. ${t('restaurant.inspectionRating')}, ${t('seo.keywords.hygieneRating')}.`;
          } else {
            title = `${t('restaurant.details')} | Kauwen of Spauwen`;
            description = t('homepage.description');
          }
          keywords = [
            t('seo.keywords.foodSafety'),
            t('seo.keywords.restaurantHygiene'),
            t('seo.keywords.foodInspection')
          ];
          break;

        case 'about':
          title = `${t('about.title')} | Kauwen of Spauwen`;
          description = t('about.subtitle');
          keywords = [t('seo.keywords.foodSafety'), t('seo.keywords.anonymousReporting')];
          break;

        case 'faq':
          title = `${t('faq.title')} | Kauwen of Spauwen`;
          description = t('faq.subtitle');
          keywords = [t('seo.keywords.foodSafety')];
          break;

        case 'howto':
          title = `${t('howTo.title')} | Kauwen of Spauwen`;
          description = t('howTo.subtitle');
          keywords = [t('seo.keywords.foodSafety')];
          break;

        case 'privacy':
          title = `${t('privacy.title')} | Kauwen of Spauwen`;
          description = t('privacy.subtitle');
          keywords = [t('seo.keywords.foodSafety')];
          break;

        default: // homepage
          title = customTitle || t('homepage.title');
          description = customDescription || t('homepage.description');
          keywords = [
            t('seo.keywords.foodSafety'),
            t('seo.keywords.restaurantHygiene'),
            t('seo.keywords.belgiumRestaurants')
          ];
      }

      // Update document title
      document.title = title;

      // Update meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);

      // Update meta keywords
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords.join(', '));

      // Update language meta tag
      document.documentElement.lang = language;

      // Update Open Graph tags
      updateOpenGraphTag('og:title', title);
      updateOpenGraphTag('og:description', description);
      updateOpenGraphTag('og:locale', language === 'nl' ? 'nl_BE' : language === 'fr' ? 'fr_BE' : 'en_US');

      // Update canonical URL
      const canonicalUrl = `https://kauwenofspauwen.be/${language}${window.location.pathname.replace(/^\/[a-z]{2}/, '')}`;
      updateCanonicalUrl(canonicalUrl);

      // Update hreflang tags
      updateHreflangTags();
    };

    updateMetaTags();
  }, [t, i18n.language, pageType, selectedEntry, customTitle, customDescription]);

  return null; // This component doesn't render anything
};

// Helper functions
const updateOpenGraphTag = (property: string, content: string) => {
  let tag = document.querySelector(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('property', property);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const updateCanonicalUrl = (url: string) => {
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
};

const updateHreflangTags = () => {
  // Remove existing hreflang tags
  const existingTags = document.querySelectorAll('link[hreflang]');
  existingTags.forEach(tag => tag.remove());

  const currentPath = window.location.pathname.replace(/^\/[a-z]{2}/, '');
  const baseUrl = 'https://kauwenofspauwen.be';

  // Add hreflang tags for all languages
  const languages = ['en', 'nl', 'fr'];
  languages.forEach(lang => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', lang);
    link.setAttribute('href', `${baseUrl}/${lang}${currentPath}`);
    document.head.appendChild(link);
  });

  // Add x-default hreflang
  const defaultLink = document.createElement('link');
  defaultLink.setAttribute('rel', 'alternate');
  defaultLink.setAttribute('hreflang', 'x-default');
  defaultLink.setAttribute('href', `${baseUrl}/en${currentPath}`);
  document.head.appendChild(defaultLink);
};

export default InternationalMetaTagsManager;